import React, { useState, useEffect } from 'react';
import { Eye, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const limit = 20;

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get(`${API}/admin/contact-messages`, { params, withCredentials: true });
      setMessages(response.data.messages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, [statusFilter, page]);

  const updateStatus = async (messageId, newStatus) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${messageId}/status?status=${newStatus}`, {}, { withCredentials: true });
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#F8F8F8]">Contact Messages</h1>
          <p className="text-[#A0A0A0] mt-1">{total} total messages</p>
        </div>
        <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(0); }}>
          <SelectTrigger className="w-[180px] bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] rounded-none">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#333333]">
            <SelectItem value="all" className="text-[#F8F8F8]">All Messages</SelectItem>
            <SelectItem value="new" className="text-[#F8F8F8]">New</SelectItem>
            <SelectItem value="read" className="text-[#F8F8F8]">Read</SelectItem>
            <SelectItem value="replied" className="text-[#F8F8F8]">Replied</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#111111] border border-[#222222]">
        {loading ? (
          <div className="p-8 text-center text-[#A0A0A0]">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="p-8 text-center text-[#A0A0A0]">No messages found</div>
        ) : (
          <div className="divide-y divide-[#222222]">
            {messages.map((message) => (
              <div key={message.id} className="p-4 hover:bg-[#1A1A1A] cursor-pointer" onClick={() => { setSelectedMessage(message); if (message.status === 'new') updateStatus(message.id, 'read'); }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-[#F8F8F8] font-medium">{message.name}</p>
                      {message.status === 'new' && <span className="w-2 h-2 bg-[#FF3B30] rounded-full"></span>}
                    </div>
                    <p className="text-[#A0A0A0] text-sm">{message.email}</p>
                    <p className="text-[#666666] text-sm mt-2 line-clamp-2">{message.message}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-[#666666] text-xs">{formatDate(message.created_at)}</p>
                    <span className={`inline-block mt-2 px-2 py-1 text-xs ${
                      message.status === 'new' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                      message.status === 'read' ? 'bg-[#FFCC00]/20 text-[#FFCC00]' :
                      'bg-[#34C759]/20 text-[#34C759]'
                    }`}>
                      {message.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#222222]">
            <p className="text-[#A0A0A0] text-sm">Page {page + 1} of {totalPages}</p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] rounded-none">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] rounded-none">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="bg-[#111111] border-[#222222] text-[#F8F8F8] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Message Details</DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-[#A0A0A0] text-sm">From</p>
                <p className="text-[#F8F8F8] font-medium">{selectedMessage.name}</p>
                <p className="text-[#00F0FF] text-sm">{selectedMessage.email}</p>
                {selectedMessage.phone && <p className="text-[#A0A0A0] text-sm">{selectedMessage.phone}</p>}
              </div>
              <div>
                <p className="text-[#A0A0A0] text-sm">Message</p>
                <p className="text-[#F8F8F8] mt-2 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-[#222222]">
                <p className="text-[#666666] text-sm">{formatDate(selectedMessage.created_at)}</p>
                <Button onClick={() => updateStatus(selectedMessage.id, 'replied')}
                  className="bg-[#34C759] text-[#050505] hover:bg-[#2DB84D] rounded-none">
                  <Check className="w-4 h-4 mr-2" /> Mark as Replied
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessagesPage;
